import { CommonModule } from '@angular/common';
import {
  ChangeDetectionStrategy,
  Component,
  computed,
  inject,
  input,
  linkedSignal,
  signal,
  viewChildren,
} from '@angular/core';
import { rxResource } from '@angular/core/rxjs-interop';
import { FormsModule } from '@angular/forms';
import { ClarityModule, ClrLoadingState } from '@clr/angular';
import { Row, Table } from 'src/app/models/ui/table';
import { HideIDColumnsPipe } from 'src/app/pipes/hide-idcolumns.pipe';
import { MrTranslatePipe } from 'src/app/pipes/mr-translate.pipe';
import { TablePrettyPrintPipe } from 'src/app/pipes/tablePrettyPrint.pipe';
import { APIService } from 'src/app/services/APIService/api.service';
import {
  FILTER_TABLE_STORE,
  FILTER_TABLE_STORE_II,
  provideFilterStore,
} from 'src/app/shared/stores/filter-table.store';
import { Gruppe } from 'src/app/types/api';
import { ColumnFilterComponent } from '../../../filter/column-filter/column-filter.component';
import { firstValueFrom, of } from 'rxjs';
import { ToastService } from 'src/app/services/toast.service';
import { AllSelectedBtnComponent } from '../../../_shared/all-selected-btn/all-selected-btn.component';
import { errorHandle } from 'src/app/misc/functions';

@Component({
  selector: 'app-gruppen-zuordnung',
  templateUrl: './gruppen-zuordnung.component.html',
  styleUrl: './gruppen-zuordnung.component.scss',
  imports: [
    CommonModule,
    ClarityModule,
    FormsModule,
    ColumnFilterComponent,
    AllSelectedBtnComponent,
    MrTranslatePipe,
    HideIDColumnsPipe,
    TablePrettyPrintPipe,
  ],
  providers: [provideFilterStore({ secondStore: true })],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class GruppenZuordnungComponent {
  private readonly toastr = inject(ToastService);
  private readonly mrTranslate = inject(MrTranslatePipe);
  private readonly api = inject(APIService);
  protected readonly filterStoreLeft = inject(FILTER_TABLE_STORE);
  protected readonly filterStoreRight = inject(FILTER_TABLE_STORE_II);

  private readonly columnFiltersLeft = viewChildren<ColumnFilterComponent>('left');
  private readonly columnFiltersRight = viewChildren<ColumnFilterComponent>('right');

  protected readonly mode = input.required<'objekte' | 'aufträge'>();

  private readonly groupsResource = rxResource({
    params: this.mode,
    stream: ({ params: mode }) => {
      switch (mode) {
        case 'aufträge':
          return this.api.getAuftragsGroups();
        case 'objekte':
          return this.api.getGroups();
        default:
          return of(undefined);
      }
    },
  });
  protected groups = computed<Gruppe[]>(() => this.groupsResource.value() ?? []);
  protected readonly selectedGroup = linkedSignal<number>(() => (this.mode(), undefined));

  private readonly itemsResource = rxResource({
    params: this.selectedGroup,
    stream: ({ params: groupId }) => {
      switch (this.mode()) {
        case 'aufträge':
          return this.api.getAuftragGroupObjects(groupId);
        case 'objekte':
          return this.api.getGroupObjects(groupId);
        default:
          return of(undefined);
      }
    },
  });
  protected readonly tableLeft = computed<Table>(
    () => this.itemsResource.value()?.leftObjects ?? { columns: [], rows: [] }
  );
  protected readonly tableRight = computed<Table>(
    () => this.itemsResource.value()?.addedObjects ?? { columns: [], rows: [] }
  );

  protected readonly trackByKey = computed<string>(() => {
    switch (this.mode()) {
      case 'aufträge':
        return 'ID';
      case 'objekte':
        return 'OSTAMMID';
    }
  });

  protected readonly selectedLeft = linkedSignal<Row[]>(() => (this.itemsResource.value(), []));
  protected readonly selectedRight = linkedSignal<Row[]>(() => (this.itemsResource.value(), []));

  protected readonly moveState = signal<ClrLoadingState>(ClrLoadingState.DEFAULT);

  constructor() {
    this.filterStoreLeft.registerFilters(this.columnFiltersLeft);
    this.filterStoreRight.registerFilters(this.columnFiltersRight);
    this.filterStoreLeft.registerTable(this.tableLeft);
    this.filterStoreRight.registerTable(this.tableRight);
  }

  protected async save(action: 'add' | 'remove') {
    this.moveState.set(ClrLoadingState.LOADING);

    try {
      const { success, error } = await firstValueFrom(this.getSaveAPI(action));
      if (!success) throw new Error(error);
      this.moveState.set(ClrLoadingState.SUCCESS);
      this.itemsResource.reload();
      this.toastr.success(this.mrTranslate.transform('Daten gespeichert'));
    } catch (er) {
      this.moveState.set(ClrLoadingState.DEFAULT);

      this.toastr.error(
        errorHandle(er),
        this.mrTranslate.transform('Beim Speichern der Gruppenberechtigung ist ein Fehler aufgetreten')
      );
    }
  }

  private getSaveAPI(action: 'add' | 'remove') {
    const groupid = this.selectedGroup();
    const items = action == 'add' ? this.selectedLeft() : this.selectedRight();
    if (!items.length) throw new Error('kein Objekt ausgewählt');

    switch (this.mode()) {
      case 'objekte': {
        switch (action) {
          case 'add':
            return this.api.setGroupObjects(
              groupid,
              +localStorage.getItem('karteFileID') || 1,
              items.map(row => ({ OSTAMMID: +row.OSTAMMID, OTYPID: +row.TID }))
            );
          case 'remove':
            return this.api.removeGroupObjects(
              groupid,
              items.map(row => +row.OSTAMMID)
            );
        }
      }
      case 'aufträge': {
        const IDs = items.map(row => +row.ID);
        switch (action) {
          case 'add':
            return this.api.setAuftragGroupObjects(groupid, IDs);
          case 'remove':
            return this.api.removeAuftragGroupObjects(IDs);
        }
      }
    }
  }
}
